import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import type { CancelFollowUpRequest } from '@/types/scheduling-api';

/**
 * POST /api/Appointment/CancelRequest
 *
 * Mirrors Flutter `SchedulingDataSourceImpl.cancelFURequest`
 * (`scheduling_data_source_impl.dart:563-582`). Drives the FU Requests
 * tab Cancel Request action (PROD-849 PR-2).
 *
 * Wire shape verified against the 2026-05-20 dev curl batch (request
 * body: `{ fuRequestId, cancellationReason: AppointmentCancellationReason }`).
 * `cancellationReason` reuses the same value-set the appointment-cancel
 * flow uses (`useAppointmentCancellationReasons`).
 *
 * **`cancellationReason.system` shape parity** (Samer PR #268 review §1):
 * the dev curl ships `"system": null` literally in the body — this
 * matches what `useCancelAppointment` does (both flows round-trip the
 * value-set entry verbatim via `cancellationReason: reasonObj`). The
 * value-set hook returns `system: null` for entries without a code
 * system AND `system: "<url>"` for entries that have one; either case
 * is shipped as-is. The .NET model binder treats null and absent the
 * same on optional reference fields, so omitting vs nulling is
 * functionally equivalent. We null because that's what the value-set
 * delivers and what the appointment-cancel flow does.
 *
 * Backend behaviour:
 *   - 2xx success per Flutter `Success('Updated')` (response body
 *     discarded by Flutter — we type the mutation as `void`)
 *   - On success: server flips the FU request status to `Cancelled`
 *     so it drops from the active-requests view + appears in
 *     cancelled-requests
 *
 * **Cache invalidation scope** (Samer PR #268 review §2): on 200,
 * invalidate the `['follow-up-requests', ...]` query family so the
 * FU tab refetches and the cancelled row drops from the active view
 * automatically. Wildcard prefix is exclusive to the FU list
 * (grep-verified 2026-05-20) — the only files using this key prefix
 * are `use-follow-up-request-list.ts`, this hook, and the FU stories.
 * No other consumer (e.g. dashboard badge, toolbar count) lives
 * under this prefix today. If `fuRequestCount` toolbar badge wiring
 * lands later, it should share this key family so it picks up the
 * same invalidation.
 */

interface CancelFollowUpRequestVariables {
  request: CancelFollowUpRequest;
}

export function useCancelFollowUpRequest(): UseMutationResult<
  void,
  Error,
  CancelFollowUpRequestVariables
> {
  const qc = useQueryClient();
  return useMutation<void, Error, CancelFollowUpRequestVariables>({
    mutationFn: ({ request }) =>
      post<void>('/api/Appointment/CancelRequest', request),
    onSuccess: () => {
      // Invalidate every page of the FU list — the cancelled row
      // drops from the active view + the count updates.
      void qc.invalidateQueries({ queryKey: ['follow-up-requests'] });
    },
  });
}
